import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import PaginationButton from './components/PaginationButton';

const Pagination = (props) => {
  const { page, pageSize, onPageChange, onPageSizeChange, pages, data } = props;
  const { pathname } = useLocation();
  const [singleBtnPerPage, setSingleBtnPerPage] = useState(false);
  const optionsPerPage = useMemo(() => [10, 20, 40, 80], []);

  const handleShow = useCallback(() => {
    window.innerWidth > 767 ? onPageSizeChange(10) : onPageSizeChange(3);
  }, [onPageSizeChange]);

  useEffect(() => {
    const index = localStorage.getItem(`indexItemPerPage_${pathname}`);
    index !== null && onPageSizeChange(optionsPerPage[index]);
    index > 0 && setSingleBtnPerPage(true);
  }, [onPageSizeChange, optionsPerPage, pathname]);

  useEffect(() => {
    window.addEventListener('resize', () => handleShow());
    return function cleanup() {
      window.removeEventListener('resize', () => handleShow());
    };
  }, [handleShow]);

  const maxRangeLength = useMemo(() => 5, []);
  const rangePadding = useMemo(() => 1, []);

  const [rangeStart, setRangeStart] = useState(0);

  const pageLabels = useMemo(() => {
    if (pageSize > 0) {
      const rangeLength = pages > maxRangeLength ? maxRangeLength : pages;

      const rangeEnd = rangeStart + rangeLength - 1;

      if (Math.abs(page - rangeEnd) < rangePadding && rangeEnd !== pages - 1) {
        setRangeStart((rangeStart) => rangeStart + Math.abs(rangePadding - Math.abs(page - rangeEnd)));
      }

      if (Math.abs(page - rangeStart) < rangePadding && rangeStart !== 0) {
        setRangeStart((rangeStart) => rangeStart - Math.abs(rangePadding - Math.abs(page - rangeStart)));
      }
      return [...Array(rangeLength).keys()].map((el) => el + rangeStart);
    } else {
      return [];
    }
  }, [pageSize, rangeStart, maxRangeLength, pages, page, rangePadding]);

  const handlePaginationButtonClick = useCallback(
    (label) => {
      switch (label) {
        case 'left':
          if (page > 0) {
            onPageChange(page - 1);
          }
          break;

        case 'right':
          if (page < pages - 1) {
            onPageChange(page + 1);
          }
          break;

        default:
          if (label - 1 !== page) {
            onPageChange(label - 1);
          }

          break;
      }
    },
    [page, onPageChange, pages]
  );

  const preserveIndexItemPerPage = useCallback(
    (index) => {
      localStorage.setItem(`indexItemPerPage_${pathname}`, index);
    },
    [pathname]
  );

  const handlePageSizeChange = useCallback(
    (label) => {
      onPageSizeChange(label);
      setSingleBtnPerPage(true);
      preserveIndexItemPerPage(optionsPerPage.indexOf(label));
    },
    [onPageSizeChange, preserveIndexItemPerPage, optionsPerPage]
  );

  const setCurrentOptionIndex = useCallback(
    (state) => {
      const index = state === 'next' ? optionsPerPage.indexOf(pageSize) + 1 : optionsPerPage.indexOf(pageSize) - 1;
      onPageSizeChange(optionsPerPage[index]);
      preserveIndexItemPerPage(index);
    },
    [optionsPerPage, pageSize, onPageSizeChange, preserveIndexItemPerPage]
  );

  return (
    <div className="pagination">
      {window.innerWidth > 768 && (
        <div className="pagination__item-per-page">
          <p>Items per page</p>
          {!singleBtnPerPage &&
            optionsPerPage.map((item) => (
              <PaginationButton
                dataTestId={'pagination-button-size'}
                key={item}
                label={item}
                active={item === pageSize}
                onClick={handlePageSizeChange}
                disabled={item / 2 >= data.length}
              />
            ))}
          {singleBtnPerPage && (
            <>
              <PaginationButton
                label={'<'}
                disabled={pageSize <= optionsPerPage[0]}
                onClick={() => setCurrentOptionIndex('prev')}
              />
              <PaginationButton
                key={pageSize}
                label={pageSize}
                active={true}
                onClick={() => setSingleBtnPerPage(!singleBtnPerPage)}
                disabled={false}
              />
              <PaginationButton
                label={'>'}
                disabled={pageSize === optionsPerPage[optionsPerPage.length - 1] || pageSize >= data.length}
                onClick={() => setCurrentOptionIndex('next')}
              />
            </>
          )}
        </div>
      )}
      <div className="pagination__labels-container" data-testid="pagination">
        <PaginationButton label="left" disabled={page === 0} onClick={handlePaginationButtonClick} />
        {pageLabels.map((el, index) => (
          <PaginationButton
            dataTestId={'pagination-button'}
            key={index}
            label={el + 1}
            active={el === page}
            onClick={handlePaginationButtonClick}
          />
        ))}
        <PaginationButton label="right" disabled={page === pages - 1} onClick={handlePaginationButtonClick} />
      </div>
    </div>
  );
};

export default Pagination;
